import React, {useEffect, useRef, useState} from "react";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import DatePicker from "react-datepicker";
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import {fetchDeliveryNoteWithDetails} from "@/api/DeliveryNotesAPI";
import LoadingElement from "@/components/Loading/LoadingElement";
import {toast} from "@/components/ui/use-toast";
import ApiHttpClient from "@/HttpClient";
import {
    DeliveryNoteProductsDetailsModel,
    DeliveryNoteProductsListResponse
} from "@/api/Models";
import { FaEdit } from "react-icons/fa";

const handleDownloadDeliveryNotePDF = async (deliveryNoteNumber: string) => {
    try {
        const response = await ApiHttpClient().get(`/deliveryNote/pdf/${deliveryNoteNumber}`);

        if (response.status === 200) {
            const {pdfBytes, fileName} = response.data;
            // Decode the base64 string
            const byteCharacters = atob(pdfBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byteArray
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', fileName || 'Aviz.pdf');
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea avizului, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea avizului', error);
    }
}

const handleDownloadDeliveryNoteConformityPDF = async (deliveryNoteNumber: string) => {
    try {
        const response = await ApiHttpClient().get(`/deliveryNote/pdf/conformity/${deliveryNoteNumber}`);

        if (response.status === 200) {
            const {pdfBytes, fileName} = response.data;
            // Decode the base64 string
            const byteCharacters = atob(pdfBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byteArray
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', fileName || 'Declaratie_de_conformitate.pdf');
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea declaratiei de conformitate, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea declaratiei de conformitate', error);
    }
}

const SearchInput: React.FC<any> = ({searchArticle, setSearchArticle, setIsNewSearch}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (value.length > 1) {
            setSearchArticle(value);
            setIsNewSearch(true);
        } else {
            setSearchArticle(value);
        }
    };

    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            value={searchArticle}
            onChange={handleChange}
            placeholder="Cauta codul sau numele unui articol..."
        />
    );
};

const transportOptions = [
    { label: "",enumValue: null},
    { label: "Transport cu masina cu temperatura controlata si depozitare in camere sau in spatii frigorifice", value: "Frigorific", enumValue: 0 },
    { label: "Transport cu masina omologata transport paine si depozitare la loc uscat si racoros", value: "Racoros", enumValue: 1 },
];

export default function DeliveryNoteDetails() {
    const queryParams = useParams();
    const deliveryNoteId = queryParams.deliveryNoteId;
    const [dataProducts, setDataProducts] = useState<DeliveryNoteProductsListResponse>();
    const detailsRef = useRef<HTMLDetailsElement>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchArticle, setSearchArticle] = useState('');
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [confirmSaveAndPrint, setConfirmSaveAndPrint] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    interface FormFields {
        delegatedName: string;
        delegateId: string;
        licenseNumber: string;
        transportDevice: string ;
        deliveryTime: Date | null;
        sealNumber: string;
    }

    const [formFields, setFormFields] = useState<FormFields>({
        delegatedName: '',
        delegateId: '',
        licenseNumber: '',
        transportDevice: '',
        deliveryTime: null,
        sealNumber: '',
    });
    const [productDetails, setProductDetails] = useState<DeliveryNoteProductsDetailsModel[]>([]);
    const pageSize = 40;

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.removeAttribute("open");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    
    const { data, isLoading, error } = useQuery({
        queryKey: ["deliveryNoteId", deliveryNoteId],
        queryFn: fetchDeliveryNoteWithDetails,
    });
    

    useEffect(() => {
        if (data) {
            const matchedTransport = transportOptions.find(
                (opt) => opt.enumValue === data.transportDevice
            );

            setFormFields({
                delegatedName: data.delegateName || '',
                delegateId: data.delegateId || '',
                transportDevice: matchedTransport?.value || '',
                licenseNumber: data.licenseNumber || '',
                deliveryTime: data.deliveryTime ? new Date(data.deliveryTime) : null,
                sealNumber: data.sealNumber || '',
            });
        }
    }, [data]);

    useEffect(() => {
        if (dataProducts?.items) {
            const parsed = dataProducts.items.map((item) => ({
                ...item,
                expirationDate: item.expirationDate ? new Date(item.expirationDate) : null,
            }));
            setProductDetails(parsed);
        }
    }, [dataProducts]);

    useEffect(() => {
        const updateDeliveryNoteProducts = async () => {
            setIsLoadingSearch(true);

            let response;

            if (searchArticle) {
                if (isNewSearch) setCurrentPage(0);

                response = await ApiHttpClient().post<DeliveryNoteProductsListResponse>('/deliveryNoteDetail/search', {
                    "advancedSearch": {
                        "fields": [
                            "articleId",
                            "article.longNameWeb"
                        ],
                        "keyword": searchArticle
                    },
                    "advancedFilter": {
                        "field" : "DeliveryNoteId",
                        "operator" : "eq",
                        "value" : data?.id
                    },
                    "pageNumber": currentPage + 1,
                    "pageSize": pageSize,
                });
            } else {
                response = await ApiHttpClient().post<DeliveryNoteProductsListResponse>(`/deliveryNoteDetail/search`, {
                    "keyword": "",
                    "advancedFilter": {
                        "field" : "DeliveryNoteId",
                        "operator" : "eq",
                        "value" : data?.id
                    },
                    "pageNumber": currentPage + 1,
                    "pageSize": pageSize,
                });
            }

            if (!response?.data) {
                toast({description: "A aparut o eroare pentru afisarea precomenzilor"});
                return <></>;
            }
            setIsLoadingSearch(false);
            setDataProducts(response.data);
        }

        if(data?.id) updateDeliveryNoteProducts();
    }, [searchArticle,currentPage, pageSize, isNewSearch, data?.id])


    if (isLoading) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement /></p>;
    if (deliveryNoteId === undefined) return <p>A aparut o eroare la extragerea datelor despre avizul de expeditie.</p>;

    if (error || !data) {
        toast({ description: "Exista o eroare la preluarea informatiilor despre acest aviz." });
        return <></>;
    }
    
    const saveAndPrint = () => {
        setConfirmSaveAndPrint(true);
    }

    const refreshPage = () => {
        window.location.reload();
    }
    
    const handleSaveAndPrintDeliveryNote = async () => {
        try {
            if (formFields.deliveryTime == null || formFields.transportDevice == null) {
                toast({ description: "Nu au fost setate valori pentru timpul de livrare sau tipul de transport." });
                return;
            }
            setIsSaving(true);
            await handleSaveDeliveryNote();
            await ApiHttpClient().patch(`/deliveryNote/status`, {
                id: data.id,
                documentStatusId: 380,
            });
            setConfirmSaveAndPrint(false);
            toast({ description: "Modificările au fost salvate cu succes." });
        } catch (error) {
            console.error('Eroare la salvare:', error);
        }
        finally {
            setIsSaving(false);
            refreshPage();
        }
    }

    const patchDeliveryNote = async () => {
        if (!data) return;

        const transportDeviceString = formFields.transportDevice;

        const payload = {
            id: data.id,
            delegatedName: formFields.delegatedName,
            delegateId: formFields.delegateId,
            transportDevice: transportDeviceString,
            licenseNumber: formFields.licenseNumber,
            deliveryTime: formFields.deliveryTime?.toISOString(),
            sealNumber: formFields.sealNumber,
            deliveryNoteDetails: productDetails.map((item) => ({
                id: item.id,
                lot: item.lot,
                expirationDate: item.expirationDate ? item.expirationDate.toISOString() : null,
                countryOrigin: item.countryOrigin
            }))
        };

        try {
            await ApiHttpClient().patch('/deliveryNote', payload);
            toast({ description: "Modificările au fost salvate cu succes." });
        } catch (error) {
            toast({ description: "Eroare la salvarea avizului." });
            console.error("PATCH error:", error);
            throw error;
        }
        finally {
            refreshPage();
        }
    };

    const handleSaveDeliveryNote = async () => {
        await patchDeliveryNote();
    };

    const updateProductDetail = (id: number, updatedFields: Partial<DeliveryNoteProductsDetailsModel>) => {
        setProductDetails((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, ...updatedFields } : item
            )
        );
    };

    return (
        <>
            <Navbar/>
            <div className="flex flex-row items-start">
                <SideMenu/>
                <div className="bg-base-100 w-full rounded-lg shadow-base-300/20 shadow-sm">
                    {!isLoadingSearch && dataProducts?.totalPages === 0 && <p className="mt-5 ml-5 mb-3">Nu au fost gasite articole pentru acest aviz.</p>}
                    <h5 className="bg-base-300/10 rounded-t-lg p-4 text-3xl font-bold">Aviz de expeditie MBA - Status {data.documentStatusName} ({data.documentStatusId})</h5>
                    <div className="w-full p-4">
                        <form className="needs-validation peer grid gap-y-4" noValidate>

                            <div className="w-full">
                                <h6 className="text-xl font-semibold">1. Date Aviz</h6>
                                <hr className="mb-4 mt-2"/>
                            </div>

                            <span
                                className="text-lg font-medium text-indigo-900"> Punctul de lucru - {data.warehouseName} </span>
                            <span
                                className="text-lg font-medium text-indigo-900"> Numele organizatiei - {data.workingPointName} </span>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label htmlFor="DeliveryNoteNumber" className="flex flex-col">
                                        <span className="text-lg font-medium text-gray-700"> Numar aviz </span>

                                        <input
                                            type="text"
                                            id="DeliveryNoteNumber"
                                            disabled
                                            placeholder="DeliveryNoteNumber"
                                            value={data.deliveryNoteNumber ? data.deliveryNoteSeries + ' ' + data.deliveryNoteNumber : "-"}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                        />
                                    </label>
                                </div>

                                <div>
                                    <label htmlFor="TransportDevice" className="flex flex-col">
                                        <span className="text-lg font-medium text-gray-700 flex flex-row"> Tipul de transport <FaEdit
                                            className="ml-2 text-indigo-900 size-3"/> </span>
                                        <select
                                            name="TransportDevice"
                                            id="TransportDevice"
                                            className="mt-0.5 w-5/6 rounded border-gray-300 shadow-sm sm:text-sm"
                                            value={formFields.transportDevice}
                                            onChange={(e) =>
                                                setFormFields({...formFields, transportDevice: e.target.value})
                                            }
                                            disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                        >
                                            {transportOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label htmlFor="DelegateName" className="flex flex-col mt-2">
                                        <span className="text-lg font-medium text-gray-700 flex flex-row">Numele delegatului <FaEdit
                                            className="ml-2 text-indigo-900 size-3"/></span>

                                        <input
                                            type="text"
                                            id="DelegateName"
                                            placeholder="DelegateName"
                                            value={formFields.delegatedName}
                                            onChange={(e) => setFormFields({
                                                ...formFields,
                                                delegatedName: e.target.value
                                            })}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                            disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                        />
                                    </label>
                                </div>
                                <div>
                                    <label htmlFor="LicenceNumber" className="flex flex-col mt-2">
                                        <span className="text-lg font-medium text-gray-700 flex flex-row"> Numar de inmatriculare <FaEdit
                                            className="ml-2 text-indigo-900 size-3"/></span>

                                        <input
                                            type="text"
                                            id="LicenceNumber"
                                            placeholder="LicenceNumber"
                                            value={formFields.licenseNumber}
                                            onChange={(e) => setFormFields({
                                                ...formFields,
                                                licenseNumber: e.target.value
                                            })}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                            disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label htmlFor="DelegateID" className="flex flex-col mt-2">
                                        <span className="text-lg font-medium text-gray-700 flex flex-row"> Serie si numar CI <FaEdit
                                            className="ml-2 text-indigo-900 size-3"/></span>

                                        <input
                                            type="text"
                                            id="DelegateID"
                                            placeholder="document serial + number"
                                            value={formFields.delegateId}
                                            onChange={(e) => setFormFields({
                                                ...formFields,
                                                delegateId: e.target.value
                                            })}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                            disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                        />
                                    </label>
                                </div>
                                <div>
                                    <label htmlFor="DeliveryTime" className="flex flex-col mt-2">
                                        <span className="text-lg font-medium text-gray-700 flex flex-row"> Timpul de livrare <FaEdit
                                            className="ml-2 text-indigo-900 size-3"/></span>

                                        <DatePicker
                                            selected={formFields.deliveryTime}
                                            onChange={(e: any) => setFormFields({
                                                ...formFields,
                                                deliveryTime: e
                                            })}
                                            showTimeSelect
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            className="mt-0.5 w-1/2 bg-white rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []},
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal"
                                            disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label htmlFor="sealNumber" className="flex flex-col mt-2">
                                        <span className="text-lg font-medium text-gray-700 flex flex-row"> Numar Sigiliu <FaEdit
                                            className="ml-2 text-indigo-900 size-3"/></span>

                                        <input
                                            type="text"
                                            id="sealNumber"
                                            placeholder="Numar Sigiliu"
                                            value={formFields.sealNumber}
                                            onChange={(e) => setFormFields({
                                                ...formFields,
                                                sealNumber: e.target.value
                                            })}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                            disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                        />
                                    </label>
                                </div>
                                
                            </div>

                            <div className="w-full mt-5">
                                <h6 className="text-xl font-semibold">2. Detaliile despre comanda</h6>
                                <hr className="mb-4 mt-2"/>
                            </div>

                            {!confirmSaveAndPrint && <div className="mb-5">
                                <SearchInput searchArticle={searchArticle} setSearchArticle={setSearchArticle}
                                             setIsNewSearch={setIsNewSearch}/>
                            </div>}

                            {!confirmSaveAndPrint && dataProducts?.items.map((deliveryNoteProduct: DeliveryNoteProductsDetailsModel) => {
                                const product = productDetails.find(p => p.id === deliveryNoteProduct.id);

                                return (
                                    <div key={deliveryNoteProduct.id}>
                                        <p className="text-lg font-semibold mt-2 text-red-800">{deliveryNoteProduct.articleName} -
                                            Cod
                                            Articol: {deliveryNoteProduct.articleId}</p>
                                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                                            <div>
                                                <label htmlFor="Lot" className="flex flex-col">
                                                    <span className="text-lg font-medium text-gray-700"> Lot </span>

                                                    <input
                                                        type="text"
                                                        id="Lot"
                                                        placeholder="Lot"
                                                        className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                                        value={product?.lot || ''}
                                                        onChange={(e) =>
                                                            updateProductDetail(deliveryNoteProduct.id, {lot: e.target.value})
                                                        }
                                                        disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                                    />
                                                </label>
                                            </div>

                                            <div>
                                                <label htmlFor="ExpirationDate" className="flex flex-col">
                                                    <span
                                                        className="text-lg font-medium text-gray-700"> Data de expirare </span>

                                                    <DatePicker
                                                        selected={product?.expirationDate ? new Date(product.expirationDate) : null}
                                                        onChange={(date) =>
                                                            updateProductDetail(deliveryNoteProduct.id, {expirationDate: date})
                                                        }
                                                        dateFormat="dd/MM/yyyy"
                                                        className="mt-0.5 w-1/2 bg-white rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                                        popperPlacement="bottom-start"
                                                        disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                                        popperModifiers={[
                                                            {
                                                                name: "preventOverflow",
                                                                options: {boundary: "viewport", tether: false},
                                                                fn: () => ({}),
                                                            },
                                                            {
                                                                name: "flip",
                                                                options: {fallbackPlacements: []},
                                                                fn: () => ({}),
                                                            },
                                                        ]}
                                                        portalId="root-portal"
                                                    />
                                                </label>
                                            </div>

                                            <div>
                                                <label htmlFor="CountryOrigin" className="flex flex-col">
                                                    <span
                                                        className="text-lg font-medium text-gray-700"> Tara de origine </span>

                                                    <input
                                                        type="text"
                                                        id="CountryOrigin"
                                                        placeholder="CountryOrigin"
                                                        value={product?.countryOrigin || ''}
                                                        onChange={(e) =>
                                                            updateProductDetail(deliveryNoteProduct.id, {countryOrigin: e.target.value})
                                                        }
                                                        className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                                        disabled={[380, 390, 400, 410].includes(data.documentStatusId)}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {confirmSaveAndPrint && <article
                                className="rounded-xl border-2 border-gray-100 mx-auto mt-4 bg-white w-1/3 justify-center items-center">
                                <div className="flex flex-col gap-4 p-4 sm:p-6 lg:p-8">
                                    <div className="block shrink-0">
                                        Mesaj de avertizare
                                    </div>

                                    <div>
                                        <h3 className="font-medium sm:text-lg">
                                            Esti sigur ca acest aviz este finalizat? Dupa aceasta actiune nu mai poate
                                            fi editat avizul
                                        </h3>

                                        <div className="flex flex-row space-x-4 mt-3">

                                            <button
                                                className="font-medium text-blue-400 px-3 py-2 bg-blue-50 rounded-lg my-auto"
                                                onClick={refreshPage}>Cancel
                                            </button>

                                            <div className="flex justify-end mx-auto space-x-3 items-center">
                                                <button
                                                    className="font-medium text-blue-400 px-3 py-2 bg-blue-50 rounded-lg my-auto"
                                                    onClick={handleSaveAndPrintDeliveryNote}
                                                    disabled={isSaving}>
                                                    {isSaving ? "Se salvează..." : "Salvează"}
                                                </button>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </article>}

                            {[380, 390, 400, 410].includes(data.documentStatusId) &&
                                <p className="mt-5 text-blue-600 underline">
                                    <button onClick={() => handleDownloadDeliveryNotePDF(data.deliveryNoteNumber)}
                                            type="button"
                                            className="mb-1 font-medium text-red-600 hover:text-red-500">
                                        Descarca Aviz
                                    </button>
                                </p>}
                            {[380, 390, 400, 410].includes(data.documentStatusId) &&
                                <p className="text-blue-600 underline">
                                    <button
                                        onClick={() => handleDownloadDeliveryNoteConformityPDF(data.deliveryNoteNumber)}
                                        type="button"
                                        className="mb-1 font-medium text-red-600 hover:text-red-500">
                                        Descarca Declaratia de conformitate
                                    </button>
                                </p>}

                            {[360, 370].includes(data.documentStatusId) && <div className="flex flex-row">
                                <div className="mt-4 mx-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveDeliveryNote}
                                        className="inline-block w-full rounded-lg bg-red-500 px-4 py-2 mt-4 mb-8 font-medium text-white sm:w-auto"
                                    >
                                        Salveaza modificarile
                                    </button>
                                </div>

                                <div className="mt-4 mx-2">
                                    <button
                                        type="button"
                                        onClick={saveAndPrint}
                                        className="inline-block w-full rounded-lg bg-green-500 px-4 py-2 mt-4 mb-8 font-medium text-white sm:w-auto"
                                    >
                                        Finalizare si Imprimare
                                    </button>
                                </div>
                            </div>}
                        </form>

                    </div>
                </div>
            </div>
        </>
    );
}
